var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { inject } from 'aurelia-dependency-injection';
import { ObserverLocator, observable, BindingEngine, BindingBehavior, ValueConverter, getContextFor } from 'aurelia-binding';
import { BoundViewFactory, TargetInstruction, ViewSlot, ViewResources, customAttribute, bindable, templateController } from 'aurelia-templating';
import { getItemsSourceExpression, isOneTime, updateOneTimeBinding, viewsRequireLifecycle, AbstractRepeater } from 'aurelia-templating-resources';
import { ReorderableRepeatStrategyLocator } from './reorderable-repeat-strategy-locator';
import { DndService } from 'bcx-aurelia-dnd';
import { EventAggregator } from 'aurelia-event-aggregator';
import { TaskQueue } from 'aurelia-task-queue';
import repeaterDndType from './repeater-dnd-type';
import { ReorderableGroupMap } from './reorderable-group-map';
let seed = 0;
const classes = (function () {
    let cache = {};
    let start = '(?:^|\\s)';
    let end = '(?:\\s|$)';
    function lookupClass(className) {
        let cached = cache[className];
        if (cached) {
            cached.lastIndex = 0;
        }
        else {
            cache[className] = cached = new RegExp(start + className + end, 'g');
        }
        return cached;
    }
    function addClass(el, className) {
        let current = el.className;
        if (!current.length) {
            el.className = className;
        }
        else if (!lookupClass(className).test(current)) {
            el.className += ' ' + className;
        }
    }
    function rmClass(el, className) {
        el.className = el.className.replace(lookupClass(className), ' ').trim();
    }
    return { add: addClass, rm: rmClass };
}());
let ReorderableRepeat = class ReorderableRepeat extends AbstractRepeater {
    constructor(ea, taskQueue, bindingEngine, dndService, viewFactory, instruction, viewSlot, viewResources, observerLocator, strategyLocator, groupMap) {
        super({
            local: 'item',
            viewsRequireLifecycle: viewsRequireLifecycle(viewFactory)
        });
        this.intention = null;
        this.type = null;
        this.repeaterId = seed;
        this.type = this.repeaterId;
        seed += 1;
        this.ea = ea;
        this.taskQueue = taskQueue;
        this.bindingEngine = bindingEngine;
        this.dndService = dndService;
        this.viewFactory = viewFactory;
        this.instruction = instruction;
        this.viewSlot = viewSlot;
        this.lookupFunctions = viewResources.lookupFunctions;
        this.observerLocator = observerLocator;
        this.strategyLocator = strategyLocator;
        this.groupMap = groupMap;
        this.ignoreMutation = false;
        this.sourceExpression = getItemsSourceExpression(this.instruction, 'reorderable-repeat.for');
        if (this.sourceExpression instanceof BindingBehavior) {
            throw new Error('BindingBehavior is not supported in reorderable-repeat');
        }
        if (this.sourceExpression instanceof ValueConverter) {
            throw new Error('ValueConverter is not supported in reorderable-repeat');
        }
        if (isOneTime(this.sourceExpression)) {
            throw new Error('oneTime binding is not supported in reorderable-repeat');
        }
        this.viewsRequireLifecycle = viewsRequireLifecycle(viewFactory);
        this.group = this._reorderableGroup();
    }
    call(context, changes) {
        this[context](this.items, changes);
    }
    bind(bindingContext, overrideContext) {
        this.scope = { bindingContext, overrideContext };
        this.matcherBinding = this._captureAndRemoveMatcherBinding();
        this.arrayObserver = this.bindingEngine.collectionObserver(this.items).subscribe(this._itemsMutated.bind(this));
        this._subsribers = [
            this.ea.subscribe('dnd:willStart', () => {
                this.intention = null;
                this.views().forEach(v => {
                    classes.rm(v.firstChild, 'reorderable-repeat-reordering');
                    classes.rm(v.firstChild, 'reorderable-repeat-dragging-me');
                });
            }),
            this.ea.subscribe('dnd:didCancel', () => {
                this.intention = null;
                this.views().forEach(v => {
                    classes.rm(v.firstChild, 'reorderable-repeat-reordering');
                    classes.rm(v.firstChild, 'reorderable-repeat-dragging-me');
                });
            }),
            this.ea.subscribe('dnd:didEnd', () => {
                this.views().forEach(v => {
                    classes.rm(v.firstChild, 'reorderable-repeat-reordering');
                    classes.rm(v.firstChild, 'reorderable-repeat-dragging-me');
                });
                if (!this.intention)
                    return;
                const { item, fromIndex, fromRepeaterId, toIndex, toRepeaterId } = this.intention;
                this.intention = null;
                const repeaterId = repeaterDndType(this.repeaterId);
                if (repeaterId !== fromRepeaterId && repeaterId !== toRepeaterId)
                    return;
                if (fromRepeaterId === toRepeaterId && fromIndex === toIndex)
                    return;
                const change = { item, fromIndex, toIndex };
                if (repeaterId === fromRepeaterId) {
                    this.items.splice(fromIndex, 1);
                    change.removedFromThisList = true;
                }
                if (repeaterId === toRepeaterId) {
                    this.items.splice(toIndex, 0, item);
                    change.insertedToThisList = true;
                }
                const afterReordering = this._reorderableAfterReorderingFunc();
                if (afterReordering) {
                    this.taskQueue.queueMicroTask(() => {
                        afterReordering(this.items, change);
                    });
                }
            }),
            this.ea.subscribe('reorderable-group:intention-changed', intention => {
                if (intention.type !== repeaterDndType(this.type))
                    return;
                if (this.intention &&
                    intention.type === this.intention.type &&
                    intention.fromIndex === this.intention.fromIndex &&
                    intention.fromRepeaterId === this.intention.fromRepeaterId &&
                    intention.toIndex === this.intention.toIndex &&
                    intention.toRepeaterId === this.intention.toRepeaterId)
                    return;
                this.intention = intention;
            })
        ];
        if (typeof this.group === 'string') {
            this.type = this.group;
        }
        else if (this.group) {
            this.group.targetProperty = 'type',
                this._typeBinding = this.group.createBinding(this);
            this._typeBinding.bind(this.scope);
        }
        this.patchedItems = [...this.items];
        this.patchedItemsChanged();
    }
    unbind() {
        this.groupMap.remove(this);
        if (this._typeBinding) {
            this._typeBinding.unbind();
            delete this._typeBinding;
        }
        if (this.arrayObserver) {
            this.arrayObserver.dispose();
            this.arrayObserver = null;
        }
        this._subsribers.forEach(s => s.dispose());
        this._subsribers = [];
        this.removeAllViews(true, true);
        this.scope = null;
        this.items = null;
        this.matcherBinding = null;
    }
    intentionChanged(newIntention) {
        if (newIntention) {
            const repeaterId = repeaterDndType(this.repeaterId);
            const { item, fromIndex, fromRepeaterId, toIndex, toRepeaterId } = newIntention;
            let patched = [...this.items];
            if (repeaterId === fromRepeaterId) {
                patched.splice(fromIndex, 1);
            }
            if (repeaterId === toRepeaterId) {
                patched.splice(toIndex, 0, item);
            }
            this.patchedItems = patched;
        }
        else {
            if (this.items) {
                this.patchedItems = [...this.items];
            }
            else {
                this.patchedItems = null;
            }
        }
    }
    itemsChanged() {
        if (!this.scope) {
            return;
        }
        if (this.arrayObserver) {
            this.arrayObserver.dispose();
            this.arrayObserver = null;
        }
        this.arrayObserver = this.bindingEngine.collectionObserver(this.items).subscribe(this._itemsMutated.bind(this));
        if (this.intention === null) {
            this.patchedItems = [...this.items];
        }
        else {
            this.intention = null;
        }
    }
    _itemsMutated() {
        if (this.intention === null) {
            this.patchedItems = [...this.items];
        }
        else {
            this.intention = null;
        }
    }
    patchedItemsChanged() {
        this._reload();
    }
    typeChanged() {
        this._reload();
    }
    _reload() {
        if (!this.scope) {
            return;
        }
        if (!this.patchedItems) {
            return;
        }
        this.strategy = this.strategyLocator.getStrategy(this.patchedItems);
        if (!this.strategy) {
            throw new Error(`Value for '${this.sourceExpression}' is non-repeatable`);
        }
        this.strategy.instanceChanged(this, this.patchedItems);
        this.taskQueue.queueMicroTask(() => {
            if (!this.scope) {
                return;
            }
            this.groupMap.remove(this);
            this.views().forEach(view => {
                this._unRegisterDnd(view);
                this._registerDnd(view);
            });
            this.groupMap.add(this);
        });
    }
    _captureAndRemoveMatcherBinding() {
        if (this.viewFactory.viewFactory) {
            const instructions = this.viewFactory.viewFactory.instructions;
            const instructionIds = Object.keys(instructions);
            for (let i = 0; i < instructionIds.length; i++) {
                const expressions = instructions[instructionIds[i]].expressions;
                if (expressions) {
                    for (let ii = 0; i < expressions.length; i++) {
                        if (expressions[ii].targetProperty === 'matcher') {
                            const matcherBinding = expressions[ii];
                            expressions.splice(ii, 1);
                            return matcherBinding;
                        }
                    }
                }
            }
        }
        return undefined;
    }
    viewCount() { return this.viewSlot.children.length; }
    views() { return this.viewSlot.children; }
    view(index) { return this.viewSlot.children[index]; }
    matcher() { return this.matcherBinding ? this.matcherBinding.sourceExpression.evaluate(this.scope, this.matcherBinding.lookupFunctions) : null; }
    addView(bindingContext, overrideContext) {
        let view = this.viewFactory.create();
        view.bind(bindingContext, overrideContext);
        this.viewSlot.add(view);
    }
    insertView(index, bindingContext, overrideContext) {
        let view = this.viewFactory.create();
        view.bind(bindingContext, overrideContext);
        this.viewSlot.insert(index, view);
    }
    moveView(sourceIndex, targetIndex) {
        this.viewSlot.move(sourceIndex, targetIndex);
    }
    removeAllViews(returnToCache, skipAnimation) {
        this.views().forEach(view => this._unRegisterDnd(view));
        return this.viewSlot.removeAll(returnToCache, skipAnimation);
    }
    removeViews(viewsToRemove, returnToCache, skipAnimation) {
        viewsToRemove.forEach(view => this._unRegisterDnd(view));
        return this.viewSlot.removeMany(viewsToRemove, returnToCache, skipAnimation);
    }
    removeView(index, returnToCache, skipAnimation) {
        this._unRegisterDnd(this.view(index));
        return this.viewSlot.removeAt(index, returnToCache, skipAnimation);
    }
    updateBindings(view) {
        let j = view.bindings.length;
        while (j--) {
            updateOneTimeBinding(view.bindings[j]);
        }
        j = view.controllers.length;
        while (j--) {
            let k = view.controllers[j].boundProperties.length;
            while (k--) {
                let binding = view.controllers[j].boundProperties[k].binding;
                updateOneTimeBinding(binding);
            }
        }
    }
    _additionalAttribute(view, attribute) {
        if (view && view.firstChild && view.firstChild.au && view.firstChild.au[attribute]) {
            return view.firstChild.au[attribute].instruction.attributes[attribute];
        }
        return this._getPlainAttribute(attribute);
    }
    _getPlainAttribute(name) {
        if (this.viewFactory && this.viewFactory.viewFactory) {
            const node = this.viewFactory.viewFactory.template.firstChild;
            if (node && node.hasAttribute(name)) {
                return node.getAttribute(name);
            }
        }
    }
    _reorderableGroup() {
        if (this.viewFactory && this.viewFactory.viewFactory) {
            const node = this.viewFactory.viewFactory.template.firstChild;
            const targetId = node.getAttribute('au-target-id');
            const instruction = this.viewFactory.viewFactory.instructions[targetId];
            if (instruction) {
                const bi = instruction.behaviorInstructions.find(bi => bi.attrName === 'reorderable-group');
                if (bi) {
                    const exp = bi.attributes && bi.attributes['reorderable-group'];
                    if (exp && typeof exp.createBinding === 'function') {
                        return exp;
                    }
                }
            }
        }
        return this._getPlainAttribute('reorderable-group');
    }
    _reorderableDirection(view) {
        let attr = this._additionalAttribute(view, 'reorderable-direction');
        if (attr && attr.sourceExpression) {
            attr = attr.sourceExpression.evaluate(this.scope);
        }
        if (typeof attr === 'string') {
            return attr.toLowerCase() || 'down';
        }
        return 'down';
    }
    _dndHandlerSelector(view) {
        let attr = this._additionalAttribute(view, 'reorderable-dnd-handler-selector');
        if (attr && attr.sourceExpression) {
            attr = attr.sourceExpression.evaluate(this.scope);
        }
        if (typeof attr === 'string') {
            return attr;
        }
    }
    _dndPreviewFunc(view) {
        const func = this._additionalAttribute(view, 'reorderable-dnd-preview');
        if (!func) {
            return null;
        }
        else if (typeof func === 'string') {
            const context = getContextFor(func, this.scope);
            let funcCall = context[func];
            if (typeof funcCall === 'function') {
                return funcCall.bind(context);
            }
            throw new Error("'reorderable-dnd-preview' must be a function or evaluate to a function");
        }
        else if (func.sourceExpression) {
            return (_, scope) => {
                return func.sourceExpression.evaluate(scope);
            };
        }
        else {
            throw new Error("'reorderable-dnd-preview' must be a function or evaluate to a function");
        }
    }
    _reorderableAfterReorderingFunc() {
        const func = this._additionalAttribute(this.view(0), 'reorderable-after-reordering');
        if (!func) {
            return null;
        }
        else if (typeof func === 'string') {
            const context = getContextFor(func, this.scope);
            let funcCall = context[func];
            if (typeof funcCall === 'function') {
                return funcCall.bind(context);
            }
            throw new Error("'reorderable-after-reordering' must be a function or evaluate to a function");
        }
        else if (func.sourceExpression) {
            return () => func.sourceExpression.evaluate(this.scope);
        }
        else {
            throw new Error("'reorderable-after-reordering' must be a function or evaluate to a function");
        }
    }
    _dndHover(location, index, direction) {
        const repeaterId = repeaterDndType(this.repeaterId);
        if (this.intention &&
            this.intention.toRepeaterId === repeaterId &&
            this.intention.toIndex === index) {
            return;
        }
        const { model } = this.dndService;
        const { mouseEndAt, targetElementRect } = location;
        const x = mouseEndAt.x - targetElementRect.x;
        const y = mouseEndAt.y - targetElementRect.y;
        let inLeastHalf;
        if (direction === 'left') {
            inLeastHalf = x > (targetElementRect.width / 2);
        }
        else if (direction === 'right') {
            inLeastHalf = x < (targetElementRect.width / 2);
        }
        else if (direction === 'up') {
            inLeastHalf = y > (targetElementRect.height / 2);
        }
        else {
            inLeastHalf = y < (targetElementRect.height / 2);
        }
        if (inLeastHalf ||
            (!this.intention && model.repeaterId === repeaterId && index === model.index)) {
            this._updateIntention(index, true);
        }
        else {
            this._updateIntention(index, false);
        }
    }
    _registerDnd(view) {
        const { local } = this;
        const el = view.firstChild;
        const item = view.bindingContext[local];
        const index = view.overrideContext.$index;
        const handlerSelector = this._dndHandlerSelector(view);
        let handler;
        if (handlerSelector) {
            handler = el.querySelector(handlerSelector);
        }
        const repeaterId = repeaterDndType(this.repeaterId);
        const direction = this._reorderableDirection(view);
        const _previewFunc = this._dndPreviewFunc(view);
        this.dndService.addSource({
            dndModel: () => ({ type: repeaterDndType(this.type), index, item, repeaterId }),
            dndPreview: _previewFunc && (() => _previewFunc(item, view)),
            dndElement: el
        }, handler && { handler });
        this.dndService.addTarget({
            dndElement: el,
            dndCanDrop: (model) => {
                if (model.type !== repeaterDndType(this.type))
                    return false;
                const { intention } = this;
                const inSameGroup = model.repeaterId === repeaterId;
                this.taskQueue.queueMicroTask(() => {
                    classes.add(el, 'reorderable-repeat-reordering');
                });
                let draggingMe;
                if (intention) {
                    draggingMe = intention.toRepeaterId === repeaterId &&
                        intention.toIndex === index;
                }
                else if (inSameGroup) {
                    draggingMe = model.index === index;
                }
                if (draggingMe) {
                    this.taskQueue.queueMicroTask(() => {
                        classes.add(el, 'reorderable-repeat-dragging-me');
                    });
                }
                return true;
            },
            dndHover: (location) => {
                this._dndHover(location, index, direction);
            },
            dndDrop() { }
        });
    }
    _unRegisterDnd(view) {
        classes.rm(view.firstChild, 'reorderable-repeat-reordering');
        classes.rm(view.firstChild, 'reorderable-repeat-dragging-me');
        this.dndService.removeSource(view.firstChild);
        this.dndService.removeTarget(view.firstChild);
    }
    _updateIntention(targetIndex, beforeTarget) {
        const { isProcessing, model } = this.dndService;
        if (!isProcessing)
            return;
        if (model.type !== repeaterDndType(this.type))
            return;
        const repeaterId = repeaterDndType(this.repeaterId);
        const isUsingGroup = model.type !== model.repeaterId;
        const inSameGroup = model.repeaterId === repeaterId;
        if (targetIndex < 0)
            return;
        let originalIndex;
        let currentIndex;
        let nextIndex;
        if (inSameGroup) {
            if (this.intention) {
                originalIndex = this.intention.fromIndex;
                currentIndex = this.intention.toIndex;
            }
            else {
                originalIndex = model.index;
                if (originalIndex < 0)
                    return;
                currentIndex = originalIndex;
            }
        }
        else {
            if (this.intention && this.intention.toRepeaterId === repeaterId) {
                originalIndex = this.intention.fromIndex;
                currentIndex = this.intention.toIndex;
            }
            else {
                originalIndex = model.index;
                if (originalIndex < 0)
                    return;
                currentIndex = targetIndex;
            }
        }
        if (currentIndex < targetIndex) {
            if (beforeTarget) {
                nextIndex = targetIndex - 1;
            }
            else {
                nextIndex = targetIndex;
            }
        }
        else {
            if (beforeTarget) {
                nextIndex = targetIndex;
            }
            else {
                nextIndex = targetIndex + 1;
            }
        }
        if (!this.intention ||
            this.intention.fromIndex !== originalIndex ||
            this.intention.fromRepeaterId !== model.repeaterId ||
            this.intention.toIndex !== nextIndex ||
            this.intention.toRepeaterId !== repeaterId) {
            this.intention = {
                type: model.type,
                item: model.item,
                fromIndex: originalIndex,
                fromRepeaterId: model.repeaterId,
                toIndex: nextIndex,
                toRepeaterId: repeaterId
            };
            if (isUsingGroup) {
                this.ea.publish('reorderable-group:intention-changed', this.intention);
            }
        }
    }
};
__decorate([
    bindable,
    __metadata("design:type", Object)
], ReorderableRepeat.prototype, "items", void 0);
__decorate([
    bindable,
    __metadata("design:type", Object)
], ReorderableRepeat.prototype, "local", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], ReorderableRepeat.prototype, "intention", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], ReorderableRepeat.prototype, "patchedItems", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], ReorderableRepeat.prototype, "type", void 0);
ReorderableRepeat = __decorate([
    customAttribute('reorderable-repeat'),
    templateController,
    inject(EventAggregator, TaskQueue, BindingEngine, DndService, BoundViewFactory, TargetInstruction, ViewSlot, ViewResources, ObserverLocator, ReorderableRepeatStrategyLocator, ReorderableGroupMap),
    __metadata("design:paramtypes", [Object, Object, Object, Object, Object, Object, Object, Object, Object, Object, Object])
], ReorderableRepeat);
export { ReorderableRepeat };

//# sourceMappingURL=reorderable-repeat.js.map
